import type { ClipboardEvent, KeyboardEvent } from "react";

import { testForNonNumericString, testForNumericString } from "app/pages/sales-intelligence/utils/strings";
import { useTranslation } from "pro-features/components/WithTranslation";

import { OPERATOR_BY_DATA_TYPE, OPERATOR_OPTIONS } from "../../../constants";
import crmEnrichmentFiltersService from "../../../services/filters";
import type { CrmEnrichmentI18nKeys } from "../../../types/translations";
import type { CrmEnrichmentJobFilterType, Operator } from "../../../types/types";
import CrmFieldDropdown from "../CrmFieldDropdown";
import FilterValueDropdown from "../FilterValueDropdown";
import InteractiveChipsTextInput from "../InteractiveChipsTextInput";
import OperatorDropdown from "../OperatorDropdown";

import {
    StyledCrmEnrichmentFilterContainer,
    StyledCrmEnrichmentFilterDeleteIconButton,
    StyledCrmEnrichmentFilterTextfield,
    StyledCrmEnrichmentFilterWrapper,
} from "./styles";

type Props = {
    translationKeys: CrmEnrichmentI18nKeys;
    filter: CrmEnrichmentJobFilterType;
    crmFieldOptions: readonly string[];
    isDeleteDisabled: boolean;
    onFilterChange: ({
        id,
        key,
        value,
    }: {
        id: number;
        key: keyof CrmEnrichmentJobFilterType;
        value: CrmEnrichmentJobFilterType[keyof CrmEnrichmentJobFilterType];
    }) => void;
    onCrmFieldFilterChange: ({ id, field }: { id: number; field: string }) => void;
    onDeleteFilter: (id: number) => void;
};

const CrmEnrichmentJobFilter = ({
    translationKeys,
    filter: { id, field, operator, singleValue, multiValue, selectedMultiValues, dataType, pickListItems },
    crmFieldOptions,
    onFilterChange,
    onCrmFieldFilterChange,
    onDeleteFilter,
    isDeleteDisabled,
}: Props) => {
    const translate = useTranslation();

    const filterID = `crm-enrichment-job-filter-${id}`;

    const operatorOptions: Operator[] = pickListItems?.length
        ? OPERATOR_BY_DATA_TYPE.Picklist
        : (OPERATOR_BY_DATA_TYPE[dataType] ?? OPERATOR_OPTIONS);

    const handleFilterChange = (
        key: keyof CrmEnrichmentJobFilterType,
        value: CrmEnrichmentJobFilterType[keyof CrmEnrichmentJobFilterType],
    ) => {
        onFilterChange({ id, key, value });
    };

    const handleMultiValueSelect = (selectedValue: string) => {
        handleFilterChange("selectedMultiValues", {
            ...selectedMultiValues,
            [selectedValue]: !selectedMultiValues[selectedValue],
        });
    };

    const handleCrmFieldChange = (fieldValue: string) => {
        onCrmFieldFilterChange({ id, field: fieldValue });
    };

    const handleOperatorChange = (operatorValue: Operator) => {
        handleFilterChange("operator", operatorValue);
    };

    const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
        if (dataType !== "Numeric") {
            return;
        }

        if (e.ctrlKey || e.metaKey) {
            return;
        }

        if (testForNonNumericString(e.key)) {
            e.preventDefault();
        }
    };

    const handleOnPaste = (e: ClipboardEvent<HTMLInputElement>): void => {
        if (dataType !== "Numeric") {
            return;
        }

        const pasteData = e.clipboardData.getData("Text");

        if (!testForNumericString(pasteData)) {
            e.preventDefault();
        }
    };

    const renderFilterValueInput = () => {
        if (crmEnrichmentFiltersService.isNoValueOperator(operator)) {
            return null;
        }

        if (pickListItems?.length) {
            return (
                <FilterValueDropdown
                    appendTo={`#${filterID}`}
                    pickListItemsOptions={pickListItems}
                    selectedMultiValues={selectedMultiValues}
                    onMultiFilterValueSelect={handleMultiValueSelect}
                />
            );
        }

        if (crmEnrichmentFiltersService.isSingleSelectOperator(operator)) {
            const translationKey = `si.crm-enrichment.job-wizard.filters.value.placeholder${dataType === "Numeric" ? ".numeric" : ""}`;

            return (
                <StyledCrmEnrichmentFilterTextfield
                    placeholder={translate(translationKey)}
                    defaultValue={singleValue}
                    onChange={(newValue) => handleFilterChange("singleValue", newValue)}
                    onKeyDown={handleOnKeyDown}
                    onPaste={handleOnPaste}
                />
            );
        }

        const translationKey = `si.crm-enrichment.job-wizard.filters.value.chips.placeholder${dataType === "Numeric" ? ".numeric" : ""}`;

        return (
            <InteractiveChipsTextInput
                type={dataType === "Numeric" ? "number" : "text"}
                chips={multiValue}
                placeholder={translate(translationKey)}
                onAddChips={(inputs: string[]) => handleFilterChange("multiValue", [...multiValue, ...inputs])}
                onRemoveChip={(input: string) =>
                    handleFilterChange(
                        "multiValue",
                        multiValue.filter((item) => item !== input),
                    )
                }
            />
        );
    };

    return (
        <StyledCrmEnrichmentFilterContainer
            key={id}
            id={filterID}
        >
            <StyledCrmEnrichmentFilterWrapper>
                <CrmFieldDropdown
                    appendTo={`#${filterID}`}
                    fieldPlaceholder={translate(translationKeys.crmFieldPlaceholder())}
                    crmField={field}
                    crmFieldOptions={crmFieldOptions}
                    onCrmFieldChange={handleCrmFieldChange}
                />
            </StyledCrmEnrichmentFilterWrapper>
            <StyledCrmEnrichmentFilterWrapper>
                <OperatorDropdown
                    operator={operator}
                    appendTo={`#${filterID}`}
                    options={operatorOptions}
                    translationKeys={translationKeys}
                    onOperatorChange={handleOperatorChange}
                />
            </StyledCrmEnrichmentFilterWrapper>

            <StyledCrmEnrichmentFilterWrapper>{renderFilterValueInput()}</StyledCrmEnrichmentFilterWrapper>

            <StyledCrmEnrichmentFilterDeleteIconButton
                iconName="delete"
                type="flatDark"
                isDisabled={isDeleteDisabled}
                onClick={() => onDeleteFilter(id)}
            />
        </StyledCrmEnrichmentFilterContainer>
    );
};

export default CrmEnrichmentJobFilter;
